import React from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./index.scss";

type Placement = "top" | "bottom" | "left" | "right";

type TooltipProps = {
    text: React.ReactNode;
    visible?: boolean;
    placement?: Placement;
    duration?: number;
    onDismiss?: () => void;
    onClick?: () => void;
    className?: string;
    children: React.ReactNode;
};

const MARGIN = 8;
const ARROW_SIZE = 6;

type Coords = {
    top: number;
    left: number;
    arrowTop: number;
    arrowLeft: number;
    actualPlacement: Placement;
};

const computePosition = (
    triggerRect: DOMRect,
    tooltipW: number,
    tooltipH: number,
    preferred: Placement
): Coords => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const placements: Placement[] = [preferred];
    const allPlacements: Placement[] = ["top", "bottom", "left", "right"];

    for(const p of allPlacements) {
        if(!placements.includes(p)) {
            placements.push(p);
        }
    }

    for(const placement of placements) {
        let top = 0;
        let left = 0;

        if(placement === "top") {
            top = triggerRect.top - tooltipH - ARROW_SIZE - 4;
            left = triggerRect.left + triggerRect.width / 2 - tooltipW / 2;
        } else if(placement === "bottom") {
            top = triggerRect.bottom + ARROW_SIZE + 4;
            left = triggerRect.left + triggerRect.width / 2 - tooltipW / 2;
        } else if(placement === "left") {
            top = triggerRect.top + triggerRect.height / 2 - tooltipH / 2;
            left = triggerRect.left - tooltipW - ARROW_SIZE - 4;
        } else {
            top = triggerRect.top + triggerRect.height / 2 - tooltipH / 2;
            left = triggerRect.right + ARROW_SIZE + 4;
        }

        left = Math.max(MARGIN, Math.min(left, vw - tooltipW - MARGIN));
        const fitsV = top >= MARGIN && top + tooltipH <= vh - MARGIN;

        if(fitsV) {
            const arrow = computeArrow(placement, triggerRect, top, left, tooltipW, tooltipH);

            return { top, left, arrowTop: arrow.top, arrowLeft: arrow.left, actualPlacement: placement };
        }
    }

    let top = triggerRect.top - tooltipH - ARROW_SIZE - 4;
    let left = triggerRect.left + triggerRect.width / 2 - tooltipW / 2;
    left = Math.max(MARGIN, Math.min(left, vw - tooltipW - MARGIN));
    top = Math.max(MARGIN, Math.min(top, vh - tooltipH - MARGIN));
    const arrow = computeArrow("top", triggerRect, top, left, tooltipW, tooltipH);

    return { top, left, arrowTop: arrow.top, arrowLeft: arrow.left, actualPlacement: "top" };
};

const computeArrow = (
    placement: Placement,
    triggerRect: DOMRect,
    tooltipTop: number,
    tooltipLeft: number,
    tooltipW: number,
    tooltipH: number
): { top: number; left: number } => {
    if(placement === "top" || placement === "bottom") {
        const centerX = triggerRect.left + triggerRect.width / 2;
        return {
            left: Math.max(10, Math.min(centerX - tooltipLeft, tooltipW - 10)),
            top: placement === "top" ? tooltipH : -ARROW_SIZE,
        };
    }

    const centerY = triggerRect.top + triggerRect.height / 2;
    return {
        top: Math.max(10, Math.min(centerY - tooltipTop, tooltipH - 10)),
        left: placement === "left" ? tooltipW : -ARROW_SIZE,
    };
};

const arrowStyle = (placement: Placement): React.CSSProperties => {
    const base: React.CSSProperties = {
        position: "absolute",
        width: 0,
        height: 0,
    };

    if(placement === "top") {
        return { ...base, borderLeft: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid transparent`, borderTop: `${ARROW_SIZE}px solid white` };
    }

    if(placement === "bottom") {
        return { ...base, borderLeft: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid transparent`, borderBottom: `${ARROW_SIZE}px solid white` };
    }

    if(placement === "left") {
        return { ...base, borderTop: `${ARROW_SIZE}px solid transparent`, borderBottom: `${ARROW_SIZE}px solid transparent`, borderLeft: `${ARROW_SIZE}px solid white` };
    }

    return { ...base, borderTop: `${ARROW_SIZE}px solid transparent`, borderBottom: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid white` };
};

const motionOffset = (placement: Placement) => {
    if(placement === "top") {
        return { y: 6 };
    }

    if(placement === "bottom") {
        return { y: -6 };
    }

    if(placement === "left") {
        return { x: 6 };
    }

    return { x: -6 };
};

const findPortalContainer = (element: HTMLElement | null): HTMLElement => {
    const el = element?.closest("[data-tooltip-container]") as HTMLElement | null;

    if(el) {
        return el;
    }

    return document.body;
};

const Tooltip = ({ text, visible = true, placement = "top", duration = 2000, onDismiss, onClick, className, children }: TooltipProps) => {
    const triggerRef = React.useRef<HTMLDivElement>(null);
    const tooltipRef = React.useRef<HTMLDivElement>(null);
    const [coords, setCoords] = React.useState<Coords | null>(null);
    const [container, setContainer] = React.useState<HTMLElement>(document.body);
    const [dismissed, setDismissed] = React.useState(false);

    React.useEffect(() => {
        setContainer(findPortalContainer(triggerRef.current));
    }, []);

    React.useEffect(() => {
        if(visible) {
            setDismissed(false);
        }
    }, [visible]);

    const measure = React.useCallback(() => {
        if(!triggerRef.current || !tooltipRef.current) {
            return;
        }

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipW = tooltipRef.current.offsetWidth;
        const tooltipH = tooltipRef.current.offsetHeight;

        const next = computePosition(triggerRect, tooltipW, tooltipH, placement);

        setCoords(previous => previous
            && previous.top === next.top
            && previous.left === next.left
            && previous.arrowTop === next.arrowTop
            && previous.arrowLeft === next.arrowLeft
            && previous.actualPlacement === next.actualPlacement
            ? previous
            : next);
    }, [placement]);

    React.useEffect(() => {
        if(!visible || dismissed) {
            setCoords(null);
            return;
        }

        let rafId: number;

        const loop = () => {
            measure();
            rafId = requestAnimationFrame(loop);
        };

        rafId = requestAnimationFrame(loop);

        return () => {
            cancelAnimationFrame(rafId);
        };
    }, [visible, dismissed, text, measure]);

    React.useEffect(() => {
        if(!visible || duration <= 0) {
            return;
        }

        const timer = setTimeout(() => {
            if(onDismiss) {
                onDismiss();
            } else {
                setDismissed(true);
            }
        }, duration);
        return () => clearTimeout(timer);
    }, [visible, text, duration, onDismiss]);

    const offset = coords ? motionOffset(coords.actualPlacement) : { y: 6 };

    return (
        <>
            <div ref={triggerRef} className={className} onClick={onClick}>
                {children}
            </div>

            {ReactDOM.createPortal(
                <AnimatePresence>
                    {visible && !dismissed && (
                        <motion.div
                            ref={tooltipRef}
                            className="fixed z-[300] cursor-pointer"
                            onClick={() => {
                                if(onDismiss) {
                                    onDismiss();
                                } else {
                                    setDismissed(true);
                                }
                            }}
                            style={{
                                top: coords ? `${coords.top}px` : 0,
                                left: coords ? `${coords.left}px` : 0,
                                visibility: coords ? "visible" : "hidden",
                            }}
                            initial={{ opacity: 0, scale: 0.9, ...offset }}
                            animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2, ease: "easeIn" } }}
                            transition={{ duration: 0.25, ease: "easeOut" }}
                        >
                            <div className="tooltip-bubble">
                                {text}
                            </div>
                            {coords && (
                                <div
                                    style={{
                                        ...arrowStyle(coords.actualPlacement),
                                        top: `${coords.arrowTop}px`,
                                        left: `${coords.arrowLeft - ARROW_SIZE}px`,
                                    }}
                                />
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>,
                container
            )}
        </>
    );
};

export default Tooltip;
